export const HIDDEN_CONNECTION_GRACE_MS = 30 * 60 * 1000;

export function installBrowserConnectionLifecycle({
  wsManager,
  getWsManager,
  simulation,
  getSimulation,
  authenticatedUser,
  validateSession,
  retryInitialization,
  windowTarget = window,
  documentTarget = document
}) {
  let hiddenSuspendTimer = null;
  const cancelHiddenSuspension = () => {
    clearTimeout(hiddenSuspendTimer);
    hiddenSuspendTimer = null;
  };
  const currentWsManager = () => getWsManager?.() ?? wsManager;
  const currentSimulation = () => getSimulation?.() ?? simulation;
  const suspendConnections = (reason) => {
    cancelHiddenSuspension();
    try {
      currentWsManager()?.suspend?.(reason);
      currentSimulation()?.suspend?.();
    } catch (error) {
      console.log('Error suspending connections', error);
    }
  };

  const resumeConnections = () => {
    cancelHiddenSuspension();
    try {
      Promise.resolve(validateSession?.()).catch(() => null);
    } catch (_) {
      // A failed validation request is not proof that the session expired.
    }
    if (!authenticatedUser) return false;

    // Reconnect synchronously. A mobile browser may leave fetch pending after
    // restoring a frozen page, so session validation must not block recovery.
    currentWsManager()?.resume?.({ forceIfStale: true });
    currentSimulation()?.resume?.();
    retryInitialization?.();

    return true;
  };

  const resumeVisibleConnections = () => {
    if (documentTarget.visibilityState !== 'hidden') {
      resumeConnections();
    }
  };

  const handlePageHide = () => suspendConnections('pagehide');
  const handlePageShow = () => resumeVisibleConnections();
  const handleOnline = () => resumeVisibleConnections();
  const handleFocus = () => resumeVisibleConnections();
  const handleVisibilityChange = () => {
    if (documentTarget.visibilityState === 'hidden') {
      // Keep healthy connections through tab switches. Actual connection
      // failures still use the manager's existing watchdog and recovery.
      if (hiddenSuspendTimer === null) {
        hiddenSuspendTimer = setTimeout(
          () => suspendConnections('hidden'),
          HIDDEN_CONNECTION_GRACE_MS
        );
      }
    } else {
      resumeConnections();
    }
  };

  windowTarget.addEventListener('pagehide', handlePageHide);
  windowTarget.addEventListener('pageshow', handlePageShow);
  windowTarget.addEventListener('online', handleOnline);
  windowTarget.addEventListener('focus', handleFocus);
  documentTarget.addEventListener('visibilitychange', handleVisibilityChange);

  // React can reinstall these listeners while the tab is already hidden.
  if (documentTarget.visibilityState === 'hidden') handleVisibilityChange();

  return () => {
    cancelHiddenSuspension();
    windowTarget.removeEventListener('pagehide', handlePageHide);
    windowTarget.removeEventListener('pageshow', handlePageShow);
    windowTarget.removeEventListener('online', handleOnline);
    windowTarget.removeEventListener('focus', handleFocus);
    documentTarget.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}
